import { useEffect, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../../lib/api";
import { toast } from "sonner";
import { useLocation, useParams } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { Button } from "../../../components/ui/button";
import { MessageCircle, Heart, ChevronRight, ChevronLeft } from "lucide-react";
import MainButton from "../../../components/MainButton";
import { Avatar } from "antd";
import * as motion from "motion/react-client";
import ContentTab from "./Tabs/Content/ContentTab";
import CustomPlayer from "../../../components/CustomPlayer";
import HomeWorkTab from "./Tabs/HomeWork/HomeWorkTab";
import DocumentsTab from "./Tabs/Documents/DocumentsTab";
import QuizTab from "./Tabs/Quiz/QuizTab";
import LoaderPage from "../../../components/LoaderPage";
import { SubLessonData } from "../../../lib/languages";

export default function ViewLesson() {
  const location = useLocation();
  const { id } = useParams();
  const lessonData = location.state?.lessonData;

  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];

  const [addLike, setAddLike] = useState(false);
  const [videoData, setVideoData] = useState({
    title: "",
    description: "",
    url: "",
    Poster: "",
    live: "",
  });

  // Use tab titles from the subLesson object
  const TabsListTitles = t.tabsListTitles;

  // Content state and fetch
  const [contents, setContents] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const fetchContents = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/lesson/${id}/contents`);
      setContents(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingContent(false);
    }
  };
  useEffect(() => {
    if (id) fetchContents();
  }, [id]);

  // Documents state and fetch
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const fetchDocuments = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/lesson/${id}/sources`);
      setDocuments(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingDocuments(false);
    }
  };
  useEffect(() => {
    if (id) fetchDocuments();
  }, [id]);

  // Assignments state and fetch
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const fetchAssignments = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/lesson/${id}/assignments`
      );
      setAssignments(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingAssignments(false);
    }
  };
  useEffect(() => {
    if (id) fetchAssignments();
  }, [id]);

  // Quizzes state and fetch
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(true);
  const fetchQuizzes = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/quizzes/lesson/${id}`);
      setQuizzes(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingQuizzes(false);
    }
  };
  useEffect(() => {
    if (id) fetchQuizzes();
  }, [id]);

  if (
    loadingContent &&
    loadingDocuments &&
    loadingAssignments &&
    loadingQuizzes
  ) {
    return <LoaderPage title={t.loaderTitle} />;
  }

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <Tabs defaultValue="content" className="w-full">
        <TabsList
          className="w-full grid grid-cols-3 sm:grid-cols-5 mb-6 bg-transparent p- border-b-2"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          {TabsListTitles.map((item, index) => (
            <TabsTrigger
              key={index}
              value={item.value}
              className="data-[state=active]:border-b-[3px] data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:rounded-none data-[state=active]:bg-transparent rounded-none border-0 data-[state=active]:shadow-none"
              onClick={() => setVideoData({})}
            >
              {item.title}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="content" dir={language === "ar" ? "rtl" : "ltr"}>
          {!videoData.live && !videoData.url ? (
            <ContentTab
              contents={contents}
              loading={loadingContent}
              setVideoData={setVideoData}
              fetchContents={fetchContents}
            />
          ) : (
            videoData.url && (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-primary mb-2"
                  onClick={() => setVideoData({})}
                >
                  <ChevronLeft /> {t.back}
                </Button>

                <CustomPlayer
                  sources={[
                    {
                      src: `${UrlImage}${videoData.url}`,
                      quality: "1080p",
                      type: "video/mp4",
                    },
                  ]}
                  poster={`${UrlImage}${lessonData?.image}`}
                  title={videoData?.title}
                />
                <div className="flex items-center gap-2 justify-between">
                  <Button variant="ghost" size="sm" className="text-primary">
                    <ChevronLeft /> {t.previous}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-primary">
                    {t.next} <ChevronRight />
                  </Button>
                </div>
                <div
                  className="break-all text-xl leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: videoData.description || "---",
                  }}
                />
              </div>
            )
          )}
        </TabsContent>

        <TabsContent
          value="description"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          <div
            className="break-all text-xl leading-relaxed"
            dangerouslySetInnerHTML={{
              __html:
                lessonData?.description?.[language] ||
                lessonData?.description?.en ||
                lessonData?.description?.ar ||
                "---",
            }}
          />
        </TabsContent>

        <TabsContent value="homeWork" dir={language === "ar" ? "rtl" : "ltr"}>
          <HomeWorkTab
            fetchAssignments={fetchAssignments}
            assignments={assignments}
          />
        </TabsContent>

        <TabsContent value="documents" dir={language === "ar" ? "rtl" : "ltr"}>
          <DocumentsTab documents={documents} fetchDocuments={fetchDocuments} />
        </TabsContent>

        <TabsContent value="messages" dir={language === "ar" ? "rtl" : "ltr"}>
          {/* Messages tab content remains unchanged */}
          <div className="grid">
            {[1, 2, 3].map((item, index) => (
              <div key={index} className="py-4 border-b">
                <div className="flex items-center gap-2">
                  <Avatar
                    size={45}
                    style={{ backgroundColor: "#fde3cf", color: "#f56a00" }}
                  >
                    م
                  </Avatar>
                  <div className="w-full">
                    <div className="flex items-center mb-2">
                      <div className="font-medium truncate flex-1">
                        محمد فتحي
                      </div>
                      <div className="text-muted-foreground text-sm">
                        منذ يوم
                      </div>
                    </div>
                    <div className="bg-muted rounded-full py-2 px-4 mr-auto ml-10 inline-block">
                      معي فاهم ازاي اكتب الرقم {index}
                    </div>
                    <div className="flex items-center mt-1 text-sm text-muted-foreground gap-5">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="font-semibold text-sm flex items-center gap-2"
                        onClick={() => setAddLike(!addLike)}
                      >
                        <Heart
                          className={`h-4 w-4 ${
                            addLike && "fill-red-500 stroke-red-500"
                          }`}
                        />
                        <span>اعجاب</span>
                      </motion.button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 !bg-transparent text-sm"
                      >
                        <span>رد</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 !bg-transparent text-sm"
                      >
                        <span>{index + 1} اجابة</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="text-end mt-4">
              <MainButton Icon={MessageCircle} title={t.messageButton} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quizs" dir={language === "ar" ? "rtl" : "ltr"}>
          <QuizTab quizzes={quizzes} fetchQuizzes={fetchQuizzes} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const PlayerSVG = (
  <svg
    width="30"
    height="31"
    viewBox="0 0 30 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_478_1440)">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M22.5 30.5H7.5C5.5109 30.4999 3.6033 29.7097 2.1968 28.3032C0.790291 26.8967 8.70173e-05 24.9891 0 23L0 8C8.70173e-05 6.0109 0.790291 4.1033 2.1968 2.6968C3.6033 1.29029 5.5109 0.500087 7.5 0.5L22.5 0.5C24.4891 0.5 26.3968 1.29018 27.8033 2.6967C29.2098 4.10322 30 6.01088 30 8V23C30 24.9891 29.2098 26.8968 27.8033 28.3033C26.3968 29.7098 24.4891 30.5 22.5 30.5Z"
        fill="#2A61E1"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M14.0625 8.9375H22.5C23.5815 8.93743 24.6217 8.52203 25.4058 7.77707C26.1899 7.03211 26.6579 6.01449 26.7132 4.93438C26.7686 3.85426 26.407 2.79414 25.7032 1.97294C24.9994 1.15174 24.0071 0.632179 22.9312 0.521562C22.7878 0.513594 22.6458 0.5 22.5 0.5H7.5C5.5109 0.500087 3.6033 1.29029 2.1968 2.6968C0.790291 4.1033 8.70173e-05 6.0109 0 8L0 23C8.70205e-05 19.2704 1.4817 15.6936 4.11891 13.0564C6.75612 10.4192 10.3329 8.93759 14.0625 8.9375Z"
        fill="#039BF0"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M22.5 15.5C21.0166 15.5 19.5666 15.9399 18.3332 16.764C17.0999 17.5881 16.1386 18.7594 15.5709 20.1299C15.0032 21.5003 14.8547 23.0083 15.1441 24.4632C15.4335 25.918 16.1478 27.2544 17.1967 28.3033C18.2456 29.3522 19.582 30.0665 21.0368 30.3559C22.4917 30.6453 23.9997 30.4968 25.3701 29.9291C26.7406 29.3614 27.9119 28.4001 28.736 27.1668C29.5601 25.9334 30 24.4834 30 23V8C30 9.98912 29.2098 11.8968 27.8033 13.3033C26.3968 14.7098 24.4891 15.5 22.5 15.5Z"
        fill="#1E4CB7"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M11.7413 8.87947C11.5992 8.79199 11.4364 8.744 11.2697 8.74045C11.1029 8.7369 10.9382 8.77792 10.7926 8.85928C10.647 8.94064 10.5257 9.05939 10.4413 9.20327C10.3569 9.34715 10.3124 9.51095 10.3125 9.67775V21.3224C10.3124 21.4892 10.3569 21.653 10.4413 21.7969C10.5257 21.9408 10.647 22.0596 10.7926 22.1409C10.9382 22.2223 11.1029 22.2633 11.2697 22.2597C11.4364 22.2562 11.5992 22.2082 11.7413 22.1207C14.1314 20.6503 18.9033 17.7135 21.2025 16.2984C21.3389 16.2145 21.4515 16.0971 21.5296 15.9574C21.6077 15.8176 21.6488 15.6602 21.6488 15.5001C21.6488 15.34 21.6077 15.1826 21.5296 15.0428C21.4515 14.9031 21.3389 14.7857 21.2025 14.7018C18.9033 13.2867 14.1314 10.3499 11.7413 8.87947Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_478_1440">
        <rect
          width="30"
          height="30"
          fill="white"
          transform="translate(0 0.5)"
        />
      </clipPath>
    </defs>
  </svg>
);
const LiveSvg = (
  <svg
    width="30"
    height="31"
    viewBox="0 0 30 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M30 19.7188C30 17.9066 28.379 16.4375 26.3793 16.4375H3.62069C1.62103 16.4375 0 17.9066 0 19.7188V27.2188C0 29.0309 1.62103 30.5 3.62069 30.5H26.3793C28.379 30.5 30 29.0309 30 27.2188V19.7188Z"
      fill="#F51E1E"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M20.6907 23V21.5938H22.7597C23.0452 21.5938 23.2769 21.3838 23.2769 21.125C23.2769 20.8663 23.0452 20.6562 22.7597 20.6562H20.1735C19.888 20.6562 19.6562 20.8663 19.6562 21.125V25.8125C19.6562 26.0712 19.888 26.2812 20.1735 26.2812H22.7597C23.0452 26.2812 23.2769 26.0712 23.2769 25.8125C23.2769 25.5538 23.0452 25.3438 22.7597 25.3438H20.6907V23.9375H22.7597C23.0452 23.9375 23.2769 23.7275 23.2769 23.4688C23.2769 23.21 23.0452 23 22.7597 23H20.6907Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M12.4134 25.8125V21.125C12.4134 20.8663 12.1817 20.6562 11.8961 20.6562C11.6106 20.6562 11.3789 20.8663 11.3789 21.125V25.8125C11.3789 26.0712 11.6106 26.2812 11.8961 26.2812C12.1817 26.2812 12.4134 26.0712 12.4134 25.8125Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M6.20703 21.125V25.8125C6.20703 26.0712 6.43876 26.2812 6.72427 26.2812H9.31048C9.596 26.2812 9.82772 26.0712 9.82772 25.8125C9.82772 25.5538 9.596 25.3438 9.31048 25.3438H7.24151V21.125C7.24151 20.8663 7.00979 20.6562 6.72427 20.6562C6.43876 20.6562 6.20703 20.8663 6.20703 21.125Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M17.092 20.9905L16.0358 24.1813L14.9796 20.9905C14.8974 20.7426 14.6088 20.6019 14.3357 20.676C14.0621 20.7505 13.9069 21.0121 13.9886 21.2596L15.5403 25.9471C15.606 26.1454 15.8072 26.2813 16.0358 26.2813C16.2645 26.2813 16.4657 26.1454 16.5314 25.9471L18.0831 21.2596C18.1648 21.0121 18.0096 20.7505 17.736 20.676C17.4629 20.6019 17.1743 20.7426 17.092 20.9905Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M19.8595 1.35913C22.23 2.78741 23.7936 5.22913 23.7936 7.99991C23.7936 10.7707 22.23 13.2124 19.8595 14.6407C19.6221 14.7841 19.5574 15.0752 19.7152 15.2908C19.8735 15.506 20.1947 15.5646 20.4326 15.4216C23.0814 13.8255 24.8281 11.096 24.8281 7.99991C24.8281 4.90382 23.0814 2.17429 20.4326 0.578193C20.1947 0.435224 19.8735 0.493818 19.7152 0.708974C19.5574 0.924599 19.6221 1.21569 19.8595 1.35913Z"
      fill="#F51E1E"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9.56934 0.578193C6.92055 2.17429 5.17383 4.90382 5.17383 7.99991C5.17383 11.096 6.92055 13.8255 9.56934 15.4216C9.80727 15.5646 10.1285 15.506 10.2868 15.2908C10.4445 15.0752 10.3799 14.7841 10.1425 14.6407C7.77194 13.2124 6.20831 10.7707 6.20831 7.99991C6.20831 5.22913 7.77194 2.78741 10.1425 1.35913C10.3799 1.21569 10.4445 0.924599 10.2868 0.708974C10.1285 0.493818 9.80727 0.435224 9.56934 0.578193Z"
      fill="#F51E1E"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M18.1427 3.70288C19.6768 4.62679 20.6886 6.20694 20.6886 7.99991C20.6886 9.79288 19.6768 11.373 18.1427 12.2969C17.9053 12.4404 17.8406 12.7315 17.9984 12.9471C18.1567 13.1623 18.4779 13.2208 18.7158 13.0779C20.5277 11.9857 21.723 10.1182 21.723 7.99991C21.723 5.88163 20.5277 4.01413 18.7158 2.92194C18.4779 2.77897 18.1567 2.83757 17.9984 3.05272C17.8406 3.26835 17.9053 3.55944 18.1427 3.70288Z"
      fill="#F51E1E"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M11.2826 2.92194C9.47075 4.01413 8.27539 5.88163 8.27539 7.99991C8.27539 10.1182 9.47075 11.9857 11.2826 13.0779C11.5206 13.2208 11.8418 13.1623 12 12.9471C12.1578 12.7315 12.0931 12.4404 11.8557 12.2969C10.3216 11.373 9.30987 9.79288 9.30987 7.99991C9.30987 6.20694 10.3216 4.62679 11.8557 3.70288C12.0931 3.55944 12.1578 3.26835 12 3.05272C11.8418 2.83757 11.5206 2.77897 11.2826 2.92194Z"
      fill="#F51E1E"
    />
    <path
      d="M14.9999 10.8125C16.7139 10.8125 18.1034 9.5533 18.1034 8C18.1034 6.4467 16.7139 5.1875 14.9999 5.1875C13.2859 5.1875 11.8965 6.4467 11.8965 8C11.8965 9.5533 13.2859 10.8125 14.9999 10.8125Z"
      fill="#F51E1E"
    />
  </svg>
);
const FileSvg = (
  <svg
    width="24"
    height="31"
    viewBox="0 0 24 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.07157 0.5C1.95345 0.5 0.214844 2.2386 0.214844 4.35672V26.6433C0.214844 28.7614 1.95345 30.5 4.07157 30.5H19.9296C22.0477 30.5 23.7863 28.7614 23.7863 26.6433V9.07143H18.4291C16.667 9.07143 15.2148 7.61932 15.2148 5.85714V0.5H4.07157ZM17.3577 0.661133V5.85714C17.3577 6.46924 17.817 6.92857 18.4291 6.92857H23.6251C23.4514 6.36196 23.1414 5.84003 22.7148 5.4135L18.8728 1.57143C18.4462 1.14491 17.9243 0.834887 17.3577 0.661133ZM6.64342 12.2857H17.3577C17.6419 12.2857 17.9144 12.3986 18.1153 12.5995C18.3162 12.8005 18.4291 13.073 18.4291 13.3571C18.4291 13.6413 18.3162 13.9138 18.1153 14.1148C17.9144 14.3157 17.6419 14.4286 17.3577 14.4286H6.64342C6.35926 14.4286 6.08673 14.3157 5.8858 14.1148C5.68487 13.9138 5.57199 13.6413 5.57199 13.3571C5.57199 13.073 5.68487 12.8005 5.8858 12.5995C6.08673 12.3986 6.35926 12.2857 6.64342 12.2857ZM6.64342 17.6429H17.3577C17.6419 17.6429 17.9144 17.7557 18.1153 17.9567C18.3162 18.1576 18.4291 18.4301 18.4291 18.7143C18.4291 18.9984 18.3162 19.271 18.1153 19.4719C17.9144 19.6728 17.6419 19.7857 17.3577 19.7857H6.64342C6.35926 19.7857 6.08673 19.6728 5.8858 19.4719C5.68487 19.271 5.57199 18.9984 5.57199 18.7143C5.57199 18.4301 5.68487 18.1576 5.8858 17.9567C6.08673 17.7557 6.35926 17.6429 6.64342 17.6429ZM6.64342 23H17.3577C17.6419 23 17.9144 23.1129 18.1153 23.3138C18.3162 23.5147 18.4291 23.7873 18.4291 24.0714C18.4291 24.3556 18.3162 24.6281 18.1153 24.829C17.9144 25.03 17.6419 25.1429 17.3577 25.1429H6.64342C6.35926 25.1429 6.08673 25.03 5.8858 24.829C5.68487 24.6281 5.57199 24.3556 5.57199 24.0714C5.57199 23.7873 5.68487 23.5147 5.8858 23.3138C6.08673 23.1129 6.35926 23 6.64342 23Z"
      fill="url(#paint0_linear_444_1681)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_444_1681"
        x1="-2.94587"
        y1="0.553572"
        x2="26.947"
        y2="30.4464"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="#00D8FF" />
        <stop offset="1" stop-color="#0000FF" />
      </linearGradient>
    </defs>
  </svg>
);
const LightSvg = (
  <svg
    width="30"
    height="31"
    viewBox="0 0 30 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M24.5275 8.46283C23.719 3.83979 19.7112 0.5 15.0002 0.5C10.2893 0.5 6.28143 3.83979 5.47296 8.46283C4.92803 11.6621 5.98266 14.8965 8.30303 17.1288C9.73788 18.5031 10.6057 20.4516 10.6057 22.4727V23.3516L12.3635 25.168H15.0002H17.637L19.3948 23.3516V22.4727C19.3948 20.4539 20.261 18.5046 21.6975 17.1288C24.0178 14.8965 25.0724 11.6621 24.5275 8.46283Z"
      fill="#2563EB"
    />
    <path
      d="M19.3945 23.3516V22.4727C19.3945 20.4539 20.2608 18.5046 21.6973 17.1288C24.0176 14.8965 25.0722 11.6621 24.5273 8.46283C23.7188 3.83979 19.7109 0.5 15 0.5V25.168H17.6367L19.3945 23.3516Z"
      fill="#204AAB"
    />
    <path
      d="M0.925781 4.36719L2.16843 3.12454L4.75584 5.71195L3.51319 6.9546L0.925781 4.36719Z"
      fill="#2563EB"
    />
    <path d="M0 9.28906H3.57422V11.0469H0V9.28906Z" fill="#2563EB" />
    <path
      d="M0.945312 15.9463L3.53016 13.3614L4.77281 14.6041L2.18796 17.1889L0.945312 15.9463Z"
      fill="#2563EB"
    />
    <path
      d="M15 23.3516H10.6055V26.1055C10.6055 28.5312 12.5742 30.5 15 30.5C17.4258 30.5 19.3945 28.5312 19.3945 26.1055V23.3516H15Z"
      fill="#EDF5FF"
    />
    <path
      d="M19.3945 26.1055V23.3516H15V30.5C17.4258 30.5 19.3945 28.5312 19.3945 26.1055Z"
      fill="#D5E8FE"
    />
    <path
      d="M25.2402 5.71387L27.8276 3.1265L29.0704 4.36927L26.483 6.95664L25.2402 5.71387Z"
      fill="#204AAB"
    />
    <path d="M26.4258 9.28906H30V11.0469H26.4258V9.28906Z" fill="#204AAB" />
    <path
      d="M25.2207 14.6074L26.4635 13.3646L29.049 15.9502L27.8063 17.193L25.2207 14.6074Z"
      fill="#204AAB"
    />
    <path
      d="M17.6367 8.41016C17.6367 9.24512 17.2348 10.0391 16.5621 10.5342C16.1344 10.8494 15.8789 11.3539 15.8789 11.8842V12.8047H14.1211V11.8842C14.1211 11.0346 14.441 10.2166 15 9.59199C15.1553 9.41855 15.3293 9.25977 15.5203 9.11914C15.7482 8.95098 15.8789 8.69258 15.8789 8.41016C15.8789 7.92559 15.4846 7.53125 15 7.53125C14.5154 7.53125 14.1211 7.92559 14.1211 8.41016V9.28906H12.3633V8.41016C12.3633 6.95645 13.5463 5.77344 15 5.77344C16.4537 5.77344 17.6367 6.95645 17.6367 8.41016Z"
      fill="white"
    />
    <path d="M14.1211 14.5625H15.8789V16.3203H14.1211V14.5625Z" fill="white" />
    <path d="M15 14.5625H15.8789V16.3203H15V14.5625Z" fill="#9A9DA5" />
    <path
      d="M17.6367 8.41016C17.6367 9.24512 17.2348 10.0391 16.5621 10.5342C16.1344 10.8494 15.8789 11.3539 15.8789 11.8842V12.8047H15V9.59199C15.1553 9.41855 15.3293 9.25977 15.5203 9.11914C15.7482 8.95098 15.8789 8.69258 15.8789 8.41016C15.8789 7.92559 15.4846 7.53125 15 7.53125V5.77344C16.4537 5.77344 17.6367 6.95645 17.6367 8.41016Z"
      fill="#9A9DA5"
    />
  </svg>
);
